import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useReduxStore } from "../../hooks/useReduxStore";
import defaultSamplePdf from "../../assets/pdfbook.pdf";

/**
 * Universal PDF URL Resolver:
 * - Detects Live URL (admin.bab-media.com) and rewrites to /live-proxy
 * - Detects Staging URL (bab-media.online) and rewrites to /media-proxy
 * - Cleans redundant double slashes (e.g. public//storage)
 * - Returns candidate URLs for resilient multi-strategy loading
 */
export const resolvePdfCandidates = (fileUrl) => {
  if (!fileUrl) return [];

  // Blob, data URL, or imported webpack asset
  if (typeof fileUrl !== "string") return [fileUrl];
  if (fileUrl.startsWith("blob:") || fileUrl.startsWith("data:")) return [fileUrl];

  // Already a relative path
  if (fileUrl.startsWith("/")) {
    return [fileUrl];
  }

  const candidates = [];

  // Live URL: admin.bab-media.com
  if (fileUrl.includes("admin.bab-media.com")) {
    const rawPath = fileUrl.replace(/^https?:\/\/[^/]+/, "");
    const cleanPath = rawPath.replace(/\/{2,}/g, "/");
    candidates.push(`/live-proxy${cleanPath}`);
    candidates.push(`/live-proxy${rawPath}`);
    candidates.push(`/pdf-stream-proxy?url=${encodeURIComponent(fileUrl)}`);
    candidates.push(fileUrl);
    return candidates;
  }

  // Staging URL: bab-media.online
  if (fileUrl.includes("bab-media.online")) {
    const rawPath = fileUrl.replace(/^https?:\/\/[^/]+/, "");
    const cleanPath = rawPath.replace(/\/{2,}/g, "/");
    candidates.push(`/media-proxy${cleanPath}`);
    candidates.push(`/media-proxy${rawPath}`);
    candidates.push(`/pdf-stream-proxy?url=${encodeURIComponent(fileUrl)}`);
    candidates.push(fileUrl);
    return candidates;
  }

  // Other remote URLs
  candidates.push(fileUrl);
  candidates.push(`/pdf-stream-proxy?url=${encodeURIComponent(fileUrl)}`);
  return candidates;
};

export const usePDFReader = () => {
  const navigate = useNavigate();
  const { getState } = useReduxStore();
  const { itemDetail } = getState("Item") || {};
  const { userData } = getState("AUTH") || {};

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [viewMode, setViewMode] = useState("single"); // 'single' | 'continuous'
  const [themeMode, setThemeMode] = useState("dark"); // 'dark' | 'sepia' | 'light'
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Loading & Data states
  const [isLoading, setIsLoading] = useState(true);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [pdfSource, setPdfSource] = useState(null);
  const [useSampleFallback, setUseSampleFallback] = useState(false);

  const containerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const rawFile = itemDetail?.file;
  const hasNoFileUploaded = !rawFile && !useSampleFallback;

  /**
   * Resilient binary fetcher:
   * Tries candidate proxy URLs in order, loads ArrayBuffer directly.
   * This completely prevents cross-origin / DNS failures inside the web worker.
   */
  const loadDocumentBinary = useCallback(async () => {
    if (useSampleFallback) {
      setPdfSource(defaultSamplePdf);
      setIsLoading(true);
      setLoadError(null);
      return;
    }

    if (!rawFile) {
      setIsLoading(false);
      return;
    }

    // Abort previous pending fetch if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setDownloadProgress(5);
    setLoadError(null);

    const candidates = resolvePdfCandidates(rawFile);
    let successBuffer = null;
    let lastError = null;

    for (const url of candidates) {
      try {
        setDownloadProgress(20);
        const response = await fetch(url, {
          signal: abortController.signal,
          headers: {
            Accept: "application/pdf, */*",
          },
        });

        if (response.ok) {
          const contentType = response.headers.get("content-type") || "";
          // Verify it's not an HTML error response (e.g. 404 redirected to index.html)
          if (
            contentType.includes("application/pdf") ||
            contentType.includes("octet-stream") ||
            url.endsWith(".pdf")
          ) {
            setDownloadProgress(60);
            const arrayBuffer = await response.arrayBuffer();
            if (arrayBuffer && arrayBuffer.byteLength > 100) {
              successBuffer = arrayBuffer;
              setDownloadProgress(95);
              break;
            }
          }
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        lastError = err;
      }
    }

    if (successBuffer) {
      // Pass binary data directly to react-pdf Document
      setPdfSource({ data: new Uint8Array(successBuffer) });
      setDownloadProgress(100);
      setLoadError(null);
    } else {
      console.warn("Failed all candidate fetches for PDF:", lastError);
      setIsLoading(false);
      setLoadError(
        "Unable to retrieve e-book from server. Connection or cross-origin restrictions prevented access."
      );
    }
  }, [rawFile, useSampleFallback]);

  useEffect(() => {
    loadDocumentBinary();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadDocumentBinary]);

  // Document load callbacks from react-pdf
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setIsLoading(false);
    setLoadError(null);
  };

  const onDocumentLoadError = (error) => {
    console.warn("react-pdf render error:", error);
    setIsLoading(false);
    setLoadError(
      error?.message || "Failed to render e-book document. File format may be corrupted."
    );
  };

  // Page navigation
  const prevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }, []);

  const nextPage = useCallback(() => {
    setPageNumber((prev) => (numPages ? Math.min(prev + 1, numPages) : prev + 1));
  }, [numPages]);

  const goToPage = useCallback(
    (page) => {
      const pageNum = parseInt(page, 10);
      if (!isNaN(pageNum) && pageNum >= 1 && (!numPages || pageNum <= numPages)) {
        setPageNumber(pageNum);
      }
    },
    [numPages]
  );

  // Zoom controls
  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(Number((prev + 0.15).toFixed(2)), 2.5));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(Number((prev - 0.15).toFixed(2)), 0.6));
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1.0);
  }, []);

  // Rotation
  const rotateClockwise = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error("Fullscreen error:", err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, []);

  // Sync fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Security: Block keyboard shortcuts (Save, Print, DevTools) and handle Arrow navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent Ctrl+S / Cmd+S (Save)
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Prevent Ctrl+P / Cmd+P (Print)
      if ((e.ctrlKey || e.metaKey) && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Prevent DevTools shortcuts F12 or Ctrl+Shift+I / Cmd+Option+I
      if (
        e.key === "F12" ||
        ((e.ctrlKey || e.metaKey) &&
          e.shiftKey &&
          (e.key === "I" ||
            e.key === "i" ||
            e.key === "C" ||
            e.key === "c" ||
            e.key === "J" ||
            e.key === "j"))
      ) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Keyboard navigation
      if (e.target.tagName !== "INPUT") {
        if (e.key === "ArrowLeft" || e.key === "PageUp") {
          prevPage();
        } else if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
          if (e.key === " ") e.preventDefault();
          nextPage();
        } else if (e.key === "+" || e.key === "=") {
          zoomIn();
        } else if (e.key === "-") {
          zoomOut();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [prevPage, nextPage, zoomIn, zoomOut]);

  // Security: Prevent context menu (right click) and drag
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  const handleDragStart = (e) => {
    e.preventDefault();
  };

  const goBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleEnableSamplePreview = () => {
    setUseSampleFallback(true);
    setLoadError(null);
    setIsLoading(true);
  };

  const handleRetry = () => {
    loadDocumentBinary();
  };

  return {
    itemDetail,
    userData,
    pdfSource,
    numPages,
    pageNumber,
    scale,
    rotation,
    viewMode,
    themeMode,
    isSidebarOpen,
    isFullscreen,
    isLoading,
    downloadProgress,
    loadError,
    hasNoFileUploaded,
    useSampleFallback,
    containerRef,
    // Actions
    prevPage,
    nextPage,
    goToPage,
    zoomIn,
    zoomOut,
    resetZoom,
    rotateClockwise,
    toggleFullscreen,
    setViewMode,
    setThemeMode,
    setIsSidebarOpen,
    goBack,
    handleContextMenu,
    handleDragStart,
    handleEnableSamplePreview,
    handleRetry,
    onDocumentLoadSuccess,
    onDocumentLoadError,
  };
};
